import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Button } from "./ui/button";
import { useTranslation } from 'react-i18next';
import { 
  ChevronLeft, 
  ChevronRight, 
  TrendingUp, 
  PieChart,
  Brain,
  Ladder,
  LayoutDashboard,
  ArrowRight,
  Utensils,
  ShoppingBag,
  Car,
  Home,
  Sparkles,
  Target,
  Shield,
  Coins,
  Crown,
  Zap,
  Unlock,
  Lock
} from 'lucide-react';

// Slide 1 - Spending Awareness Hook
const SpendingAwarenessSlide = ({ t, onCTA }) => {
  return (
    <div 
      className="w-full h-full min-h-screen flex items-center justify-center relative overflow-hidden pt-20 md:pt-24 pb-20 md:pb-24"
      style={{
        backgroundImage: `url('https://customer-assets.emergentagent.com/job_8bfe693c-b82f-4c3e-a4e0-8fee4488e8fb/artifacts/4v44g3wm_alexander-mils-lCPhGxs7pww-unsplash.jpg')`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
      }}
    >
      {/* Dark overlay for text readability */}
      <div className="absolute inset-0 bg-black/60"></div>
      
      {/* Top gradient to blend with header */}
      <div className="absolute top-0 left-0 right-0 h-32 bg-gradient-to-b from-black via-black/80 to-transparent"></div>

      {/* Content */}
      <div className="relative z-10 text-center max-w-3xl mx-auto px-4">
        <h1 className="text-2xl md:text-3xl lg:text-4xl font-bold text-white mb-4 leading-tight drop-shadow-lg">
          {t('onboarding.slide1.headline')}
        </h1>
        <p className="text-base md:text-lg text-gray-200 mb-6 drop-shadow-md">
          {t('onboarding.slide1.body')}
        </p>
        <Button
          onClick={onCTA}
          className="bg-yellow-500 hover:bg-yellow-600 text-black font-bold px-6 py-3 text-base rounded-xl transition-all duration-300 shadow-lg hover:shadow-xl transform hover:scale-105 inline-flex items-center gap-2"
        >
          {t('onboarding.slide1.cta')}
          <ArrowRight className="w-5 h-5" />
        </Button>
        <p className="text-gray-300 text-xs mt-3 drop-shadow-md">
          {t('onboarding.slide1.secondary')}
        </p>
      </div>
    </div>
  );
};

// Slide 2 - Financial Patterns
const FinancialPatternsSlide = ({ t, onCTA }) => {
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun'];
  const data = [1800, 2100, 1950, 2400, 2200, 2850];
  const maxValue = Math.max(...data);

  return (
    <div className="w-full h-full min-h-screen flex items-center justify-center px-4 pt-20 md:pt-24 pb-20 md:pb-24 relative">
      {/* Top gradient to blend with header */}
      <div className="absolute top-0 left-0 right-0 h-32 bg-gradient-to-b from-black via-black/80 to-transparent z-10"></div>
      
      <div className="flex flex-col lg:flex-row items-center justify-center gap-4 lg:gap-12 w-full max-w-6xl mx-auto px-4 relative z-0">
        {/* Left: Visual - Trend Chart */}
        <div className="flex-1 max-w-sm w-full order-2 lg:order-1">
          <div className="bg-gray-900/80 backdrop-blur-sm rounded-2xl p-4 border border-gray-700 shadow-2xl">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-white font-semibold text-sm">{t('onboarding.slide2.chartTitle')}</h3>
              <div className="flex items-center gap-1 text-red-400 text-xs">
                <TrendingUp className="w-3 h-3" />
                +34%
              </div>
            </div>

            {/* Bar Chart - Compact */}
            <div className="flex items-end justify-between h-24 mb-2 gap-1">
              {data.map((value, index) => {
                const heightPercent = (value / maxValue) * 100;
                return (
                  <div key={index} className="flex-1 flex flex-col justify-end h-full">
                    <div 
                      className="w-full bg-gradient-to-t from-yellow-600 to-yellow-400 rounded-t-sm"
                      style={{ height: `${heightPercent}%` }}
                    />
                  </div>
                );
              })}
            </div>
            <div className="flex justify-between">
              {months.map((month, index) => (
                <span key={index} className="text-gray-500 text-[10px] flex-1 text-center">{month}</span>
              ))}
            </div>

            {/* Insight Card - Compact */}
            <div className="mt-3 bg-gradient-to-r from-red-900/50 to-orange-900/50 rounded-lg p-2.5 border border-red-700/50">
              <div className="flex items-start gap-2">
                <div className="w-7 h-7 bg-red-500/20 rounded-full flex items-center justify-center flex-shrink-0">
                  <Sparkles className="w-3.5 h-3.5 text-red-400" />
                </div>
                <div>
                  <p className="text-white font-medium text-xs">{t('onboarding.slide2.insight')}</p>
                  <p className="text-gray-400 text-[10px] mt-0.5">{t('onboarding.slide2.insightDetail')}</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Right: Content */}
        <div className="flex-1 text-center lg:text-left max-w-xl order-1 lg:order-2">
          <h1 className="text-2xl md:text-3xl lg:text-4xl font-bold text-white mb-4 leading-tight">
            {t('onboarding.slide2.headline')}
          </h1>
          <p className="text-base md:text-lg text-gray-300 mb-6">
            {t('onboarding.slide2.body')}
          </p>
          <Button
            onClick={onCTA}
            className="bg-yellow-500 hover:bg-yellow-600 text-black font-bold px-6 py-3 text-base rounded-xl transition-all duration-300 shadow-lg hover:shadow-xl transform hover:scale-105 inline-flex items-center gap-2"
          >
            {t('onboarding.slide2.cta')}
            <ArrowRight className="w-5 h-5" />
          </Button>
        </div>
      </div>
    </div>
  );
};

// Slide 3 - Wealth Identity Quiz
const WealthIdentitySlide = ({ t, onCTA }) => {
  const identities = [
    { name: t('onboarding.slide3.identities.survivor'), icon: Shield, color: '#ef4444', desc: t('onboarding.slide3.identities.survivorDesc') },
    { name: t('onboarding.slide3.identities.worker'), icon: Target, color: '#f97316', desc: t('onboarding.slide3.identities.workerDesc') },
    { name: t('onboarding.slide3.identities.accumulator'), icon: Coins, color: '#22c55e', desc: t('onboarding.slide3.identities.accumulatorDesc') },
    { name: t('onboarding.slide3.identities.investor'), icon: Crown, color: '#3b82f6', desc: t('onboarding.slide3.identities.investorDesc') },
  ];

  return (
    <div className="w-full h-full min-h-screen flex items-center justify-center px-4 pt-20 md:pt-24 pb-20 md:pb-24 relative">
      {/* Top gradient to blend with header */}
      <div className="absolute top-0 left-0 right-0 h-32 bg-gradient-to-b from-black via-black/80 to-transparent z-10"></div>
      
      <div className="flex flex-col items-center justify-center gap-2 w-full max-w-6xl mx-auto px-4 relative z-0">
        {/* Content - Top */}
        <div className="text-center max-w-xl w-full">
          <div className="inline-flex items-center gap-2 bg-purple-500/20 text-purple-400 px-2 py-0.5 rounded-full text-[10px] md:text-xs font-medium mb-1">
            <Brain className="w-3 h-3" />
            {t('onboarding.slide3.badge')}
          </div>
          <h1 className="text-lg md:text-2xl lg:text-3xl font-bold text-white mb-1 leading-tight">
            {t('onboarding.slide3.headline')}
          </h1>
          <p className="text-xs md:text-base text-gray-300 mb-2">
            {t('onboarding.slide3.body')}
          </p>
          <Button
            onClick={onCTA}
            className="bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 text-white font-bold px-4 py-2 text-xs md:text-sm rounded-xl transition-all duration-300 shadow-lg hover:shadow-xl transform hover:scale-105 inline-flex items-center gap-2"
          >
            {t('onboarding.slide3.cta')}
            <ArrowRight className="w-4 h-4" />
          </Button>
        </div>

        {/* Visual - Identity Cards - Horizontal row on mobile */}
        <div className="w-full max-w-md">
          <div className="flex justify-center gap-2">
            {identities.map((identity, index) => {
              const Icon = identity.icon;
              return (
                <div 
                  key={index}
                  className="bg-gray-900/80 backdrop-blur-sm rounded-lg p-1.5 md:p-2 border border-gray-700 text-center flex-shrink-0 w-16 md:w-20"
                >
                  <div 
                    className="w-6 h-6 md:w-7 md:h-7 rounded-lg flex items-center justify-center mx-auto mb-0.5"
                    style={{ backgroundColor: `${identity.color}20` }}
                  >
                    <Icon className="w-3 h-3 md:w-3.5 md:h-3.5" style={{ color: identity.color }} />
                  </div>
                  <h4 className="text-white font-medium text-[8px] md:text-[10px] leading-tight">{identity.name}</h4>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};

// Slide 4 - Wealth Ladder
const WealthLadderSlide = ({ t, onCTA }) => {
  // 7 stages matching the actual Ladder of Wealth module
  const stages = [
    { name: t('onboarding.slide4.stages.abundance'), color: '#8B5CF6', icon: Crown },      // 7 - Purple
    { name: t('onboarding.slide4.stages.freedom'), color: '#1E3A8A', icon: Unlock },       // 6 - Navy blue
    { name: t('onboarding.slide4.stages.security'), color: '#059669', icon: TrendingUp }, // 5 - Green
    { name: t('onboarding.slide4.stages.stability'), color: '#84CC16', icon: Coins },     // 4 - Lime green
    { name: t('onboarding.slide4.stages.independence'), color: '#F59E0B', icon: Shield }, // 3 - Amber
    { name: t('onboarding.slide4.stages.productivity'), color: '#EA580C', icon: Target }, // 2 - Orange
    { name: t('onboarding.slide4.stages.dependence'), color: '#DC2626', icon: Shield },   // 1 - Red
  ];

  return (
    <div className="w-full h-full min-h-screen flex items-center justify-center px-4 pt-20 md:pt-24 pb-20 md:pb-24 relative">
      {/* Top gradient to blend with header */}
      <div className="absolute top-0 left-0 right-0 h-32 bg-gradient-to-b from-black via-black/80 to-transparent z-10"></div>
      
      <div className="flex flex-col lg:flex-row items-center justify-center gap-0 lg:gap-12 w-full max-w-6xl mx-auto px-4 relative z-0">
        {/* Left: Visual - Ladder */}
        <div className="flex-1 max-w-sm w-full order-2 lg:order-1">
          <div className="bg-gray-900/80 backdrop-blur-sm rounded-lg p-1 md:p-4 border border-gray-700 shadow-2xl">
            <h3 className="text-white font-semibold mb-0.5 text-center text-[9px] md:text-sm">{t('onboarding.slide4.chartTitle')}</h3>
            
            {/* Vertical Ladder - Ultra compact on mobile */}
            <div className="relative flex flex-col">
              {stages.map((stage, index) => {
                const Icon = stage.icon;
                const isHighlighted = index === 4; // Highlight Independence stage (index 4 = stage 3)
                return (
                  <div 
                    key={index}
                    className={`flex items-center gap-1 h-[22px] md:h-auto md:py-1.5 px-1 md:px-2 rounded-md transition-all duration-300 ${
                      isHighlighted ? 'bg-yellow-500/20 border border-yellow-500/50' : 'hover:bg-gray-800/50'
                    }`}
                  >
                    {/* Stage number */}
                    <div 
                      className="w-[14px] h-[14px] md:w-6 md:h-6 rounded-full flex items-center justify-center flex-shrink-0 text-[7px] md:text-xs font-bold"
                      style={{ 
                        backgroundColor: stage.color,
                        color: index >= 5 ? 'white' : 'black'
                      }}
                    >
                      {7 - index}
                    </div>
                    
                    {/* Icon - hidden on very small screens */}
                    <Icon 
                      className="w-2.5 h-2.5 md:w-3.5 md:h-3.5 flex-shrink-0 hidden sm:block" 
                      style={{ color: stage.color }}
                    />
                    
                    {/* Stage name */}
                    <span 
                      className={`text-[8px] md:text-xs font-medium flex-1 ${isHighlighted ? 'text-yellow-400' : 'text-gray-300'}`}
                    >
                      {stage.name}
                    </span>

                    {/* "You" indicator */}
                    {isHighlighted && (
                      <span className="text-[6px] md:text-xs bg-yellow-500 text-black px-0.5 rounded-full font-bold animate-pulse">
                        {t('onboarding.slide4.you')}
                      </span>
                    )}
                  </div>
                );
              })}
            </div>

            {/* Bottom hint - hidden on mobile */}
            <p className="text-gray-500 text-[10px] md:text-xs text-center mt-1 italic hidden md:block">
              {t('onboarding.slide4.hint')}
            </p>
          </div>
        </div>

        {/* Right: Content */}
        <div className="flex-1 text-center lg:text-left max-w-xl order-1 lg:order-2">
          <h1 className="text-sm md:text-3xl lg:text-4xl font-bold text-white mb-0.5 md:mb-4 leading-tight">
            {t('onboarding.slide4.headline')}
          </h1>
          <p className="text-[9px] md:text-lg text-gray-300 mb-1 md:mb-6">
            {t('onboarding.slide4.body')}
          </p>
          <Button
            onClick={onCTA}
            className="bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-600 hover:to-emerald-600 text-white font-bold px-2.5 md:px-6 py-1 md:py-3 text-[9px] md:text-base rounded-xl transition-all duration-300 shadow-lg hover:shadow-xl transform hover:scale-105 inline-flex items-center gap-1"
          >
            {t('onboarding.slide4.cta')}
            <ArrowRight className="w-2.5 h-2.5 md:w-5 md:h-5" />
          </Button>
        </div>
      </div>
    </div>
  );
};

// Slide 5 - ProsperMind Vision (Final CTA)
const ProsperMindVisionSlide = ({ t, onCTA, onLogin }) => {
  const features = [
    { icon: PieChart, label: t('onboarding.slide5.features.spending'), color: '#ef4444' },
    { icon: TrendingUp, label: t('onboarding.slide5.features.savings'), color: '#22c55e' },
    { icon: Target, label: t('onboarding.slide5.features.freedom'), color: '#3b82f6' },
  ];

  return (
    <div 
      className="w-full h-full min-h-screen flex items-center justify-center relative overflow-hidden pt-20 md:pt-24 pb-20 md:pb-24"
      style={{
        backgroundImage: `url('https://customer-assets.emergentagent.com/job_11a77923-0552-4fea-aa1b-255a5b8cf611/artifacts/y41bx08k_jakub-zerdzicki-zR7nFjjIAWE-unsplash.jpg')`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
      }}
    >
      {/* Dark overlay for text readability */}
      <div className="absolute inset-0 bg-black/60"></div>
      
      {/* Top gradient to blend with header */}
      <div className="absolute top-0 left-0 right-0 h-32 bg-gradient-to-b from-black via-black/80 to-transparent"></div>

      {/* Content */}
      <div className="relative z-10 flex flex-col lg:flex-row items-center justify-center gap-2 lg:gap-12 w-full max-w-6xl mx-auto px-4">
        {/* Left: Content */}
        <div className="flex-1 text-center lg:text-left max-w-xl">
          <h1 className="text-xl md:text-3xl lg:text-4xl font-bold text-white mb-2 md:mb-4 leading-tight drop-shadow-lg">
            {t('onboarding.slide5.headline')}
          </h1>
          <p className="text-sm md:text-lg text-gray-200 mb-3 md:mb-6 drop-shadow-md">
            {t('onboarding.slide5.body')}
          </p>
          
          <div className="flex flex-col sm:flex-row gap-3 justify-center lg:justify-start">
            <Button
              onClick={onCTA}
              className="bg-yellow-500 hover:bg-yellow-600 text-black font-bold px-5 md:px-6 py-2.5 md:py-3 text-sm md:text-base rounded-xl transition-all duration-300 shadow-lg hover:shadow-xl transform hover:scale-105 inline-flex items-center justify-center gap-2 whitespace-nowrap"
            >
              {t('onboarding.slide5.cta')}
              <ArrowRight className="w-4 h-4 md:w-5 md:h-5" />
            </Button>
            <Button
              onClick={onLogin}
              variant="outline"
              className="border-gray-400 text-white hover:bg-white/20 px-5 md:px-6 py-2.5 md:py-3 text-sm md:text-base rounded-xl transition-all duration-300 whitespace-nowrap"
            >
              {t('onboarding.slide5.login')}
            </Button>
          </div>
        </div>

        {/* Right: Visual - Dashboard Preview */}
        <div className="flex-1 max-w-sm w-full">
          <div className="bg-gray-900/90 backdrop-blur-sm rounded-xl p-3 md:p-4 border border-gray-700 shadow-2xl">
            <div className="flex items-center gap-2 mb-2 md:mb-3">
              <LayoutDashboard className="w-4 h-4 md:w-5 md:h-5 text-yellow-400" />
              <h3 className="text-white font-semibold text-xs md:text-sm">{t('onboarding.slide5.dashboardTitle')}</h3>
            </div>

            {/* Mini dashboard */}
            <div className="space-y-1.5 md:space-y-2">
              {features.map((feature, index) => {
                const Icon = feature.icon;
                return (
                  <div 
                    key={index}
                    className="bg-gray-800/70 rounded-md p-2 md:p-2.5 border border-gray-700/50 flex items-center gap-2"
                  >
                    <div 
                      className="w-7 h-7 md:w-9 md:h-9 rounded-md flex items-center justify-center flex-shrink-0"
                      style={{ backgroundColor: `${feature.color}20` }}
                    >
                      <Icon className="w-3.5 h-3.5 md:w-4 md:h-4" style={{ color: feature.color }} />
                    </div>
                    <div className="flex-1">
                      <p className="text-white font-medium text-[10px] md:text-xs">{feature.label}</p>
                      <div className="h-1 md:h-1.5 bg-gray-700 rounded-full mt-0.5 overflow-hidden">
                        <div 
                          className="h-full rounded-full"
                          style={{ 
                            width: `${60 + index * 15}%`,
                            backgroundColor: feature.color
                          }}
                        />
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Slide 6 - Freedom Wealth Banner (Original Hero)
const FreedomWealthSlide = ({ t, onCTA }) => {
  return (
    <div 
      className="w-full h-full min-h-screen flex items-center justify-center relative overflow-hidden pt-20 md:pt-24 pb-20 md:pb-24"
      style={{
        backgroundImage: `url('https://customer-assets.emergentagent.com/job_8d69d7c1-df0b-49de-be30-49ea53b150fa/artifacts/imrdqrhz_Cierre%20Freedom%20Wealth.png')`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
      }}
    >
      {/* Dark overlay for text readability */}
      <div className="absolute inset-0 bg-black/50"></div>
      
      {/* Top gradient to blend with header */}
      <div className="absolute top-0 left-0 right-0 h-32 bg-gradient-to-b from-black via-black/80 to-transparent"></div>

      {/* Content */}
      <div className="relative z-10 text-center max-w-3xl mx-auto px-4">
        <h1 className="text-2xl md:text-3xl lg:text-4xl font-bold text-white mb-4 leading-tight drop-shadow-lg">
          {t('onboarding.slide6.headline1')}
          <span className="text-yellow-400"> {t('onboarding.slide6.headline2')}</span>
          <br />
          <span className="text-white/90">{t('onboarding.slide6.headline3')}</span>
        </h1>
        <p className="text-base md:text-lg text-gray-200 mb-6 drop-shadow-md max-w-xl mx-auto">
          {t('onboarding.slide6.body')}
        </p>
        <Button
          onClick={onCTA}
          className="bg-yellow-500 hover:bg-yellow-600 text-black font-bold px-6 py-3 text-base rounded-xl transition-all duration-300 shadow-lg hover:shadow-xl transform hover:scale-105 inline-flex items-center gap-2"
        >
          {t('onboarding.slide6.cta')}
          <ArrowRight className="w-5 h-5" />
        </Button>
      </div>
    </div>
  );
};

// Main Carousel Component
const OnboardingCarousel = ({ onStartQuiz, onLogin }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [currentSlide, setCurrentSlide] = useState(0);
  const [touchStart, setTouchStart] = useState(null);
  const [touchEnd, setTouchEnd] = useState(null);
  const containerRef = useRef(null);

  const totalSlides = 6;
  const minSwipeDistance = 50;

  const handleCTA = useCallback(() => {
    navigate('/login?action=register');
  }, [navigate]);

  const handleLogin = useCallback(() => {
    navigate('/login');
  }, [navigate]);

  const goToSlide = useCallback((index) => {
    if (index >= 0 && index < totalSlides) {
      setCurrentSlide(index);
    }
  }, [totalSlides]);

  const nextSlide = useCallback(() => {
    if (currentSlide < totalSlides - 1) {
      setCurrentSlide(prev => prev + 1);
    }
  }, [currentSlide, totalSlides]);

  const prevSlide = useCallback(() => {
    if (currentSlide > 0) {
      setCurrentSlide(prev => prev - 1);
    }
  }, [currentSlide]);

  // Touch handlers for swipe
  const onTouchStart = (e) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };

  const onTouchMove = (e) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const onTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > minSwipeDistance;
    const isRightSwipe = distance < -minSwipeDistance;
    
    if (isLeftSwipe) {
      nextSlide();
    } else if (isRightSwipe) {
      prevSlide();
    }
  };

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'ArrowRight') nextSlide();
      if (e.key === 'ArrowLeft') prevSlide();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [nextSlide, prevSlide]);

  const slides = [
    <SpendingAwarenessSlide key={0} t={t} onCTA={handleCTA} />,
    <FinancialPatternsSlide key={1} t={t} onCTA={handleCTA} />,
    <WealthIdentitySlide key={2} t={t} onCTA={handleCTA} />,
    <WealthLadderSlide key={3} t={t} onCTA={handleCTA} />,
    <FreedomWealthSlide key={4} t={t} onCTA={handleCTA} />,
    <ProsperMindVisionSlide key={5} t={t} onCTA={handleCTA} onLogin={handleLogin} />,
  ];

  return (
    <div className="min-h-screen bg-black text-white flex flex-col">
      {/* Header */}
      <header className="bg-black/80 backdrop-blur-sm fixed top-0 left-0 right-0 z-50 border-b border-gray-800/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-center items-center py-4 relative">
            {/* Centered Logo */}
            <div className="flex items-center justify-center">
              <img 
                src="https://customer-assets.emergentagent.com/job_prospermind-charts/artifacts/bhqmeu3t_Logo%20ProsperMind.png" 
                alt="ProsperMind"
                className="h-16 md:h-20 lg:h-24 w-auto"
              />
            </div>
            {/* Login Button - Positioned absolute right */}
            <Button
              onClick={handleLogin}
              data-testid="header-login-btn"
              className="absolute right-0 bg-transparent border border-gray-600 text-gray-300 hover:bg-gray-800 hover:text-white px-4 py-2 rounded-lg transition-colors"
            >
              {t('landing.login')}
            </Button>
          </div>
        </div>
      </header>

      {/* Main Carousel Area */}
      <main 
        ref={containerRef}
        className="flex-1 flex items-center justify-center relative overflow-hidden"
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
      >
        {/* Background gradient - only visible for slides without bg images */}
        <div className="absolute inset-0 bg-gradient-to-br from-gray-900 via-black to-gray-900" />
        
        {/* Decorative elements */}
        <div className="absolute top-1/4 left-1/4 w-64 h-64 bg-yellow-500/5 rounded-full blur-3xl" />
        <div className="absolute bottom-1/4 right-1/4 w-64 h-64 bg-purple-500/5 rounded-full blur-3xl" />

        {/* Slides Container */}
        <div 
          className="relative z-10 w-full h-full transition-transform duration-500 ease-out"
          style={{ transform: `translateX(-${currentSlide * 100}%)` }}
        >
          <div className="flex h-full">
            {slides.map((slide, index) => (
              <div 
                key={index}
                className="min-w-full h-full flex items-center justify-center"
              >
                {slide}
              </div>
            ))}
          </div>
        </div>

        {/* Navigation Arrows */}
        <button
          onClick={prevSlide}
          className={`absolute left-4 top-1/2 -translate-y-1/2 z-20 w-12 h-12 rounded-full bg-gray-800/80 backdrop-blur-sm border border-gray-700 flex items-center justify-center transition-all duration-300 ${
            currentSlide === 0 ? 'opacity-30 cursor-not-allowed' : 'hover:bg-gray-700 hover:scale-110'
          }`}
          disabled={currentSlide === 0}
          aria-label="Previous slide"
        >
          <ChevronLeft className="w-6 h-6 text-white" />
        </button>

        <button
          onClick={nextSlide}
          className={`absolute right-4 top-1/2 -translate-y-1/2 z-20 w-12 h-12 rounded-full bg-gray-800/80 backdrop-blur-sm border border-gray-700 flex items-center justify-center transition-all duration-300 ${
            currentSlide === totalSlides - 1 ? 'opacity-30 cursor-not-allowed' : 'hover:bg-gray-700 hover:scale-110'
          }`}
          disabled={currentSlide === totalSlides - 1}
          aria-label="Next slide"
        >
          <ChevronRight className="w-6 h-6 text-white" />
        </button>
      </main>

      {/* Bottom Navigation */}
      <footer className="fixed bottom-0 left-0 right-0 z-50 bg-black/80 backdrop-blur-sm border-t border-gray-800/50">
        <div className="max-w-7xl mx-auto px-4 py-2 md:py-4">
          {/* Dots Indicator */}
          <div className="flex justify-center items-center gap-2 md:gap-3">
            {Array.from({ length: totalSlides }).map((_, index) => (
              <button
                key={index}
                onClick={() => goToSlide(index)}
                className={`transition-all duration-300 rounded-full ${
                  index === currentSlide 
                    ? 'w-6 md:w-8 h-2 md:h-3 bg-yellow-500' 
                    : 'w-2 md:w-3 h-2 md:h-3 bg-gray-600 hover:bg-gray-500'
                }`}
                aria-label={`Go to slide ${index + 1}`}
              />
            ))}
          </div>

          {/* Progress text */}
          <p className="text-center text-gray-500 text-xs mt-1 md:mt-2">
            {currentSlide + 1} / {totalSlides}
          </p>

          {/* Trust Signal + Copyright Legal - hidden on small mobile, visible on taller screens */}
          <div className="mt-2 md:mt-4 pt-2 md:pt-4 border-t border-gray-800/50 hidden sm:block">
            {/* Trust Signal */}
            <div className="flex items-center justify-center gap-4 mb-3">
              <div className="flex items-center gap-1.5 text-xs text-gray-500">
                <Shield className="w-3 h-3 text-emerald-500" />
                <span>{t('trust.secure')}</span>
              </div>
              <div className="w-px h-3 bg-gray-700" />
              <div className="flex items-center gap-1.5 text-xs text-gray-500">
                <span>{t('trust.neverSold')}</span>
              </div>
              <div className="w-px h-3 bg-gray-700" />
              <Link to="/privacy" className="text-xs text-gray-500 hover:text-yellow-500 transition-colors">
                {t('trust.privacy')}
              </Link>
              <div className="w-px h-3 bg-gray-700" />
              <Link to="/terms" className="text-xs text-gray-500 hover:text-yellow-500 transition-colors">
                {t('trust.terms')}
              </Link>
            </div>
            
            <p className="text-center text-gray-600 text-xs">
              © 2026 ProsperMind LLC. All Rights Reserved.
            </p>
            <p className="text-center text-gray-700 text-xs mt-1 max-w-2xl mx-auto">
              The content, design, and information on this website are protected by copyright and other intellectual property laws. 
              Unauthorized use, reproduction, or distribution of any materials from this site is strictly prohibited without prior written consent from ProsperMind LLC.
            </p>
          </div>
        </div>
      </footer>

      {/* CSS Animations */}
      <style>{`
        @keyframes slideIn {
          from {
            width: 0;
          }
          to {
            width: var(--target-width, 100%);
          }
        }

        @keyframes growUp {
          from {
            height: 0;
          }
          to {
            height: var(--target-height, 100%);
          }
        }

        @keyframes fadeSlideIn {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
      `}</style>
    </div>
  );
};

export default OnboardingCarousel;
